import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Search as SearchIcon, ArrowRight, Loader2 } from "lucide-react";
import { aiSearch } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { Tx } from "../context/AutoTranslateContext";

const SUGGESTIONS_EN = [
  "I have constant lower back pain radiating to my leg",
  "Frequent migraines and tension headaches",
  "Stomach pain after eating, acid reflux",
  "Anxiety and difficulty sleeping",
  "High blood pressure and family history of heart disease",
  "Skin rash, itchy and dry patches on arms",
];

const SUGGESTIONS_AR = [
  "ألم مستمر في أسفل الظهر يمتد إلى الساق",
  "صداع نصفي متكرر وصداع توتري",
  "ألم في المعدة بعد الأكل وارتجاع حمضي",
  "قلق وصعوبة في النوم",
  "ارتفاع ضغط الدم وتاريخ عائلي لأمراض القلب",
  "طفح جلدي مع حكة وبقع جافة على الذراعين",
];

const Search = () => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const { t, lang } = useLanguage();

  const suggestions = lang === "ar" ? SUGGESTIONS_AR : SUGGESTIONS_EN;

  const submit = async (q) => {
    const text = (q ?? query).trim();
    if (!text) return;
    setQuery(text);
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const data = await aiSearch(text);
      setResult(data);
    } catch (e) {
      setError(t("search_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="search-page" className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-forest/5 text-forest rounded-full px-4 py-1.5 text-xs mb-5">
          <Sparkles className="w-3.5 h-3.5" />
          {t("search_pill")}
        </div>
        <h1 data-testid="search-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest font-bold tracking-tight mb-5">
          {t("search_title")}
        </h1>
        <p className="text-mute leading-relaxed max-w-2xl mx-auto">{t("search_subtitle")}</p>
      </div>

      <form data-testid="search-form" onSubmit={(e) => { e.preventDefault(); submit(); }} className="clay-surface rounded-3xl p-3 sm:p-4 mb-6">
        <div className="flex items-stretch gap-2">
          <div className="flex-1 flex items-center gap-3 px-4">
            <SearchIcon className="w-5 h-5 text-mute flex-shrink-0" />
            <input
              data-testid="search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search_placeholder")}
              className="flex-1 bg-transparent border-none outline-none py-4 text-base placeholder:text-mute/60"
            />
          </div>
          <button
            data-testid="search-submit"
            type="submit"
            disabled={loading || !query.trim()}
            className="bg-forest text-sand hover:bg-sage disabled:opacity-50 rounded-2xl px-6 py-4 text-sm font-medium transition-all flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("searching")}
              </>
            ) : (
              <>
                {t("search_btn")}
                <ArrowRight className="w-4 h-4 rtl:rotate-180" />
              </>
            )}
          </button>
        </div>
      </form>

      {!result && !loading && (
        <div data-testid="search-suggestions" className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-3">{t("try_these")}</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                data-testid={`suggestion-${s.slice(0, 20).replace(/\s+/g, "-")}`}
                onClick={() => submit(s)}
                className="bg-cream hover:bg-white border border-forest/10 text-forest px-4 py-2.5 rounded-full text-xs sm:text-sm transition hover:-translate-y-0.5"
              >
                {lang === "ar" ? s : <Tx>{s}</Tx>}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-clay/10 border border-clay/30 rounded-2xl p-5 text-clay text-sm mb-6">{error}</div>
      )}

      {loading && (
        <div data-testid="search-loading" className="text-center py-16">
          <Loader2 className="w-8 h-8 text-forest animate-spin mx-auto mb-4" />
          <p className="text-mute">{t("researching")}</p>
        </div>
      )}

      {result && (
        <div data-testid="search-results" className="space-y-6 animate-fade-in-up">
          <div className="clay-surface rounded-3xl p-7">
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-3">{t("researcher_note")}</p>
            <p data-testid="search-summary" className="text-ink leading-relaxed text-lg font-heading">{result.summary}</p>
          </div>

          {result.matching_illness_ids?.length > 0 ? (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{t("suggested_entries")}</p>
              <div className="grid sm:grid-cols-2 gap-4 stagger">
                {result.matching_illness_ids.map((id) => (
                  <Link
                    key={id}
                    to={`/illness/${id}`}
                    data-testid={`result-card-${id}`}
                    className="clay-surface rounded-2xl p-6 lift-on-hover group flex items-center justify-between gap-4"
                  >
                    <span className="font-heading text-xl text-forest font-semibold capitalize">
                      {id.replace(/_/g, " ")}
                    </span>
                    <ArrowRight className="w-5 h-5 text-mute group-hover:text-forest group-hover:translate-x-1 rtl:rotate-180 transition" />
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <p data-testid="no-results" className="text-center text-mute py-8">{t("no_results")}</p>
          )}

          <p className="text-xs text-clay italic text-center pt-4 border-t border-forest/10">{result.disclaimer}</p>
        </div>
      )}
    </div>
  );
};

export default Search;
